import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { Trash2, Plus, Minus, ShoppingBag } from "lucide-react";
import { resolveImage } from "@/lib/image";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const navigate = useNavigate();
  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="cart-empty">
        <ShoppingBag className="w-14 h-14 text-[#8A9A86] mx-auto mb-4"/>
        <h2 className="font-heading text-3xl text-[#1A2421]">Your cart is empty</h2>
        <p className="text-[#5C6B64] mt-2 mb-6">Pick up some royal Filipino flavors.</p>
        <div className="flex gap-3 justify-center">
          <Link to="/restaurant" className="btn-mart">Browse Restaurant</Link>
          <Link to="/grocery" className="border border-[#2C4A3B] text-[#2C4A3B] px-7 py-3 rounded-full text-sm font-semibold">Browse Mart</Link>
        </div>
      </div>
    );
  }
  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-8 py-10 grid lg:grid-cols-3 gap-8" data-testid="cart-page">
      <div className="lg:col-span-2 space-y-3">
        <h1 className="font-heading text-4xl text-[#1A2421] mb-4">Your Cart</h1>
        {items.map((it, idx) => (
          <div key={idx} className="mart-card p-4 flex gap-4 items-center" data-testid={`cart-item-${idx}`}>
            <div className="w-20 h-20 rounded bg-[#F2EFEB] bg-cover bg-center flex-shrink-0" style={{backgroundImage: `url(${resolveImage(it.image)})`}}/>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className={`badge-${it.item_type === 'restaurant' || it.item_type === 'lechon' || it.item_type === 'catering' ? 'royal' : 'fresh'}`}>
                  {it.item_type === 'restaurant' ? 'CHOW' : it.item_type === 'grocery' ? 'MART' : it.item_type.toUpperCase()}
                </span>
              </div>
              <div className="font-heading text-lg text-[#1A2421] mt-1">{it.name}</div>
              {it.options?.rice && <div className="text-xs text-[#5C6B64]">Rice: {it.options.rice}</div>}
              {it.options?.combo_entrees?.length > 0 && (
                <div className="text-xs text-[#5C6B64]">Entrees: {it.options.combo_entrees.join(", ")}</div>
              )}
              {it.options?.size && <div className="text-xs text-[#5C6B64]">Size: {it.options.size}</div>}
              <div className="text-sm text-[#5C6B64]">${it.unit_price.toFixed(2)} each</div>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid={`cart-dec-${idx}`} onClick={() => updateQty(idx, it.quantity - 1)} className="w-8 h-8 rounded-full border border-[#2C4A3B] text-[#2C4A3B] flex items-center justify-center"><Minus className="w-3 h-3"/></button>
              <span className="w-8 text-center font-semibold">{it.quantity}</span>
              <button data-testid={`cart-inc-${idx}`} onClick={() => updateQty(idx, it.quantity + 1)} className="w-8 h-8 rounded-full border border-[#2C4A3B] text-[#2C4A3B] flex items-center justify-center"><Plus className="w-3 h-3"/></button>
            </div>
            <div className="font-heading text-lg w-20 text-right text-[#2C4A3B]">${(it.unit_price * it.quantity).toFixed(2)}</div>
            <button data-testid={`cart-remove-${idx}`} onClick={() => removeItem(idx)} className="text-[#8B0000] hover:bg-red-50 w-9 h-9 rounded-full flex items-center justify-center"><Trash2 className="w-4 h-4"/></button>
          </div>
        ))}
      </div>
      <aside className="lg:sticky lg:top-24 h-fit mart-card p-6 space-y-3" data-testid="cart-summary">
        <h3 className="font-heading text-2xl text-[#1A2421]">Order summary</h3>
        <div className="flex justify-between text-[#5C6B64]"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
        <div className="text-xs text-[#5C6B64]">Taxes calculated at checkout (California 9.75% on prepared food).</div>
        <button data-testid="cart-checkout" onClick={() => navigate("/checkout")} className="btn-mart w-full">Proceed to Checkout</button>
      </aside>
    </div>
  );
}
